import { FC } from 'react';

import { EnvelopeWithStatus } from 'types/Envelope';

import { useTranslate } from 'hooks/useTranslate';

import { formatEnvelopeDate } from 'modules/envelopes/utils/formatEnvelopeDate';

import { EnvelopeStatus } from 'modules/envelopes/components/EnvelopesTable/envelopeStatus';
import EnvelopeBadges from '../../../components/EnvelopeBadges';
import EnvelopeDetailsActions from './EnvelopeDetailsActions';
import { getEnvelopeStatusData } from '../../../utils/getEnvelopeStatusData';

type EnvelopeDetailsSummaryCardProps = {
  envelope: EnvelopeWithStatus;
  completedSigners: number;
  totalSigners: number;
  refetchEnvelope: () => void;
};

const EnvelopeDetailsSummaryCard: FC<EnvelopeDetailsSummaryCardProps> = ({
  envelope,
  completedSigners,
  totalSigners,
  refetchEnvelope,
}) => {
  const { t } = useTranslate();
  const { envelopeStatus } = envelope;

  const { statusLabel, statusBadge, statusValue } = getEnvelopeStatusData({
    ...envelope,
    status: envelope?.envelopeStatus?.envelopeStatus.toUpperCase() || '',
  });

  return (
    <div className="card rounded-xl mb-4">
      <div className="card-inner">
        <div className="d-flex align-items-start justify-content-between mb-4">
          <h6 className="card-title mb-3">{t('Summary')}</h6>

          {EnvelopeBadges.Status && (
            <EnvelopeBadges.Status
              t={t}
              style={statusBadge}
              text={statusLabel}
              status={statusValue as keyof typeof envelopeStatus}
            />
          )}
        </div>

        <div className="row gap-4">
          <div className="col-md-4">
            <div className="d-flex align-items-center gap-2">
              <em className="icon ni ni-calendar text-muted mr-2 fs-16px" />
              <div>
                <p className="text-sm font-weight-bold mb-0">
                  {t('Sent Date')}
                </p>
                <p className="text-sm text-muted mb-0">
                  {formatEnvelopeDate(envelope.creationDate)}
                </p>
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="d-flex align-items-center gap-2">
              <em className="icon ni ni-clock text-muted mr-2 fs-16px" />
              <div>
                <p className="text-sm font-weight-bold mb-0">{t('Expires')}</p>
                <p className="text-sm text-muted mb-0">
                  {formatEnvelopeDate(
                    envelopeStatus?.envelopeInfo.expireDateTime ||
                      envelope.creationDate.getTime() + 30 * 24 * 60 * 60 * 1000
                  )}
                </p>
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="d-flex align-items-center gap-2">
              <em className="icon ni ni-user text-muted mr-2 fs-16px" />
              <div>
                <p className="text-sm font-weight-bold mb-0">{t('Progress')}</p>
                <p className="text-sm text-muted mb-0">
                  {completedSigners} {t('of')} {totalSigners} {t('signed')}
                </p>
              </div>
            </div>
          </div>
        </div>

        <hr />

        <div className="row">
          <div className="col-12">
            <EnvelopeDetailsActions
              envelope={envelope}
              status={statusValue as EnvelopeStatus}
              refetchEnvelope={refetchEnvelope}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnvelopeDetailsSummaryCard;
